import { __read } from "tslib";
import { mergeClasses, tokens } from '@fluentui/react-components';
import { ImageOff24Filled, ImageOff24Regular, ImageOff32Filled, ImageOff32Regular, } from '@fluentui/react-icons';
import AppBarIcons from '@office-start/appbar-icons';
import React from 'react';
import { FlyoutIconSize, FlyoutTileIconSize, OutlineIconFilterID, TileIconSizeDefault, TileIconSizeWide, TileOutlineIconSizeWide, ViewMode, } from './Constants';
import { useIconStyles } from './TileIcon.styles';
/**
 * Gets icon size based on the type of icon it is.
 * @param viewMode - The view mode of the icon (default App Bar, wide App Bar, flyout).
 * @param isOutlineIcon - Whether the icon is an outline icon.
 * @param isFullBleedIcon - Whether the icon is a full bleed icon.
 * @returns The CSS string for the dimensions of the icon.
 */
function getIconSize(viewMode, isOutlineIcon, isFullBleedIcon) {
    var iconSize = TileIconSizeDefault;
    if (viewMode === ViewMode.Wide) {
        if (isOutlineIcon) {
            iconSize = TileOutlineIconSizeWide;
        }
        else {
            iconSize = TileIconSizeWide;
        }
    }
    else if (viewMode === ViewMode.Flyout) {
        if (isFullBleedIcon) {
            iconSize = FlyoutTileIconSize;
        }
        else {
            iconSize = FlyoutIconSize;
        }
    }
    return iconSize;
}
/**
 * Renders an icon for display in a LaunchTile
 *
 * @param icon: InternalIcon to render
 * @param isSelected: Used to determine correct coloring. True if the tile is selected. False otherwise.
 * @param label: Text to display underneath icon (OPTIONAL)
 * @returns
 */
export var TileIcon = function (_a) {
    var id = _a.id, icon = _a.icon, isSelected = _a.isSelected, label = _a.label, useHubColorForSelectedTileLabel = _a.useHubColorForSelectedTileLabel, viewMode = _a.viewMode, brandingColor = _a.brandingColor, showDotIndicator = _a.showDotIndicator, useOutlineIconFilter = _a.useOutlineIconFilter;
    var _b = __read(React.useState(false), 2), iconError = _b[0], setIconError = _b[1];
    var onIconLoad = React.useCallback(function () {
        setIconError(false);
    }, []);
    var onIconError = React.useCallback(function () {
        setIconError(true);
    }, []);
    var iconStyles = useIconStyles();
    var isFullBleedIcon = !useOutlineIconFilter &&
        icon != null &&
        'isFullBleed' in icon &&
        !!icon.isFullBleed;
    var iconSize = getIconSize(viewMode || ViewMode.Default, !!useOutlineIconFilter, isFullBleedIcon);
    var primaryFillColor = isSelected
        ? brandingColor || tokens.colorNeutralForeground3BrandSelected
        : viewMode === ViewMode.Flyout
            ? tokens.colorNeutralForeground1
            : tokens.colorNeutralForeground3;
    var labelColor = isSelected
        ? useHubColorForSelectedTileLabel
            ? tokens.colorNeutralForeground3BrandSelected
            : tokens.colorNeutralForeground3Selected
        : viewMode === ViewMode.Flyout
            ? tokens.colorNeutralForeground1
            : tokens.colorNeutralForeground3;
    var FallBackIconComponent24 = isSelected
        ? ImageOff24Filled
        : ImageOff24Regular;
    var FallBackIconComponent32 = isSelected
        ? ImageOff32Filled
        : ImageOff32Regular;
    var FallBackIconComponent = viewMode == ViewMode.Flyout
        ? FallBackIconComponent32
        : FallBackIconComponent24;
    var outlineIconFilter = React.useMemo(function () {
        return (React.createElement("svg", { className: iconStyles.outlineIconFilter },
            React.createElement("defs", null,
                React.createElement("filter", { id: "".concat(OutlineIconFilterID, "-").concat(id) },
                    React.createElement("feFlood", { floodColor: primaryFillColor, result: "flood" }),
                    React.createElement("feComposite", { in: "SourceGraphic", in2: "flood", operator: "arithmetic", k1: "1", k2: "0", k3: "0", k4: "0" })))));
    }, [iconStyles.outlineIconFilter, id, primaryFillColor]);
    var iconElement = React.useMemo(function () {
        if (!icon || iconError) {
            return (React.createElement(FallBackIconComponent, { primaryFill: primaryFillColor, size: viewMode === ViewMode.Wide ? 'wide' : 'default' }));
        }
        if (icon && !('__typename' in icon)) {
            return icon;
        }
        switch (icon.__typename) {
            case 'InternalIcon': {
                var IconComponent = AppBarIcons[icon.name];
                if (!IconComponent) {
                    IconComponent = FallBackIconComponent;
                }
                return (React.createElement(IconComponent, { primaryFill: primaryFillColor, size: viewMode === ViewMode.Wide ? 'wide' : 'default' }));
            }
            case 'RemoteImage': {
                return (React.createElement(React.Fragment, null,
                    React.createElement("img", { src: icon.src, alt: label, className: mergeClasses(isFullBleedIcon &&
                            viewMode === ViewMode.Flyout &&
                            iconStyles.flyoutIconImageFullBleed, isFullBleedIcon &&
                            viewMode !== ViewMode.Flyout &&
                            iconStyles.tileImageFullBleed), style: {
                            width: iconSize,
                            height: iconSize,
                            filter: useOutlineIconFilter
                                ? "url(#".concat("".concat(OutlineIconFilterID, "-").concat(id), ")")
                                : undefined,
                        }, "data-clicklog": "custom", onLoad: onIconLoad, onError: onIconError }),
                    useOutlineIconFilter && outlineIconFilter));
            }
            default: {
                return undefined;
            }
        }
    }, [
        icon,
        iconError,
        primaryFillColor,
        viewMode,
        label,
        isFullBleedIcon,
        iconStyles.flyoutIconImageFullBleed,
        iconStyles.tileImageFullBleed,
        iconSize,
        useOutlineIconFilter,
        id,
        onIconLoad,
        onIconError,
        outlineIconFilter,
        FallBackIconComponent,
    ]);
    return (React.createElement("div", { className: mergeClasses(iconStyles.tile, viewMode === ViewMode.Wide && iconStyles.wideTile, viewMode === ViewMode.Flyout && iconStyles.flyoutTile) },
        React.createElement("div", { className: mergeClasses(viewMode === ViewMode.Flyout && iconStyles.flyoutIcon, viewMode === ViewMode.Flyout && iconStyles.flyoutIconMatted) }, iconElement),
        viewMode !== ViewMode.Default && (React.createElement("span", { className: mergeClasses(iconStyles.label, viewMode === ViewMode.Flyout && iconStyles.flyoutLabel), style: {
                color: labelColor,
            } },
            showDotIndicator ? ' \u25CF ' : null,
            label))));
};
//# sourceMappingURL=TileIcon.js.map